import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import AuthNavbar from "./AuthNavbar";
import LoadingState from "../Common/LoadingState";
import { getVerificationStatus, resendEmailVerification } from "../../services/auth";
import "./Auth.css";

/**
 * Props for the VerificationStatus component
 */
interface VerificationStatusProps {
  /** Email address to check verification status for */
  email?: string;
  /** Phone number to check verification status for */
  phone?: string;
  /** Callback when user clicks email verification action */
  onEmailVerificationClick?: () => void;
  /** Callback when user clicks phone verification action */
  onPhoneVerificationClick?: () => void;
  /** Callback when user clicks login action */
  onLoginClick?: () => void;
}

/**
 * Verification data structure as returned by the API
 */
interface VerificationData {
  email_verified: boolean;
  phone_verified: boolean;
  can_access_dashboard: boolean;
}

/**
 * Loading states for different operations
 */
enum LoadingStateEnum {
  LOADING,
  LOADED,
  ERROR,
  RESENDING_EMAIL,
}

/**
 * VerificationStatus Component
 * 
 * Displays user verification progress and next steps according to requirements 6.1-6.5:
 * - 6.1: Shows email verification status with resend option
 * - 6.2: Shows phone verification status with retry option  
 * - 6.3: Shows confirmation when fully verified
 * - 6.4: Provides clear error messages and next steps
 * - 6.5: Shows success confirmation for each verification step
 * 
 * @param props - VerificationStatusProps
 */
const VerificationStatus: React.FC<VerificationStatusProps> = ({
  email,
  phone,
  onEmailVerificationClick,
  onPhoneVerificationClick,
  onLoginClick,
}) => {
  const navigate = useNavigate();

  // State management
  const [loadingState, setLoadingState] = useState<LoadingStateEnum>(LoadingStateEnum.LOADING);
  const [verificationData, setVerificationData] = useState<VerificationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [lastStatusCheck, setLastStatusCheck] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * Loads verification status from the API
   * Implements requirement 6.1, 6.2, 6.3 for status checking
   */
  const loadVerificationStatus = useCallback(async (forceRefresh = false) => {
    // Debounce calls (only check every 3 seconds unless forced)
    const now = Date.now();
    if (!forceRefresh && isLoading) {
      console.log("Verification status check already in progress, skipping");
      return;
    }
    
    if (!forceRefresh && (now - lastStatusCheck) < 3000) {
      console.log("Recent verification status check, skipping");
      return;
    }

    if (!email && !phone) {
      // Get email/phone from localStorage if not provided
      const storedEmail = localStorage.getItem("email");
      const storedPhone = localStorage.getItem("phone");
      
      if (!storedEmail && !storedPhone) {
        setLoadingState(LoadingStateEnum.ERROR);
        setError("No email or phone provided to check verification status");
        return;
      }
      
      // Use stored values if props not provided
      const emailToUse = email || storedEmail;
      const phoneToUse = phone || storedPhone;
      
      try {
        setIsLoading(true);
        setLoadingState(LoadingStateEnum.LOADING);
        setError(null);
        setSuccess(null);

        const result = await getVerificationStatus(emailToUse || undefined, phoneToUse || undefined);
        
        setLastStatusCheck(now);

        if (result.success && result.data) {
          setVerificationData(result.data);
          setLoadingState(LoadingStateEnum.LOADED);
          setRetryCount(0); // Reset retry count on success
        } else {
          // Only show error if we've tried multiple times
          if (retryCount >= 2) {
            setLoadingState(LoadingStateEnum.ERROR);
            setError(result.error || "Failed to load verification status");
          } else {
            // Retry once automatically to handle temporary network issues
            setRetryCount(prev => prev + 1);
            setTimeout(() => {
              loadVerificationStatus(true); // Force refresh on retry
            }, 1500);
          }
        }
      } catch (err) {
        // Only show error if we've tried multiple times
        if (retryCount >= 2) {
          setLoadingState(LoadingStateEnum.ERROR);
          setError("Network error. Please check your connection and try again.");
        } else {
          // Retry once automatically
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            loadVerificationStatus(true); // Force refresh on retry
          }, 1500);
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      setIsLoading(true);
      setLoadingState(LoadingStateEnum.LOADING);
      setError(null);
      setSuccess(null);

      const result = await getVerificationStatus(email, phone);
      
      setLastStatusCheck(now);

      if (result.success && result.data) {
        setVerificationData(result.data);
        setLoadingState(LoadingStateEnum.LOADED);
        setRetryCount(0); // Reset retry count on success
      } else {
        // Only show error if we've tried multiple times
        if (retryCount >= 2) {
          setLoadingState(LoadingStateEnum.ERROR);
          setError(result.error || "Failed to load verification status");
        } else {
          // Retry once automatically to handle temporary network issues
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            loadVerificationStatus(true); // Force refresh on retry
          }, 1500);
        }
      }
    } catch (err) {
      // Only show error if we've tried multiple times
      if (retryCount >= 2) {
        setLoadingState(LoadingStateEnum.ERROR);
        setError("Network error. Please check your connection and try again.");
      } else {
        // Retry once automatically
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          loadVerificationStatus(true); // Force refresh on retry
        }, 1500);
      }
    } finally {
      setIsLoading(false);
    }
  }, [email, phone, retryCount, lastStatusCheck, isLoading]);

  // Load verification status on component mount
  useEffect(() => {
    loadVerificationStatus();
  }, []); // Only run once on mount

  // Refresh when email/phone props change (but not on every render)
  useEffect(() => {
    if (email || phone) {
      loadVerificationStatus(true); // Force refresh when props change
    }
  }, [email, phone]); // Only depend on email/phone props

  /**
   * Handles resending email verification
   * Implements requirement 6.1 for email verification resend option
   */
  const handleResendEmailVerification = async () => {
    if (!email) {
      setError("Email address is required to resend verification");
      return;
    }

    try {
      setLoadingState(LoadingStateEnum.RESENDING_EMAIL);
      setError(null);
      setSuccess(null);

      const result = await resendEmailVerification(email);

      if (result.success) {
        setSuccess("Verification email sent! Please check your inbox and spam folder.");
        setLoadingState(LoadingStateEnum.LOADED);
        // Auto-refresh status after a delay to check if user verified
        setTimeout(() => {
          loadVerificationStatus();
        }, 3000);
      } else {
        setError(result.error || "Failed to resend verification email");
        setLoadingState(LoadingStateEnum.LOADED);
      }
    } catch (err) {
      setError("Failed to resend verification email. Please try again.");
      setLoadingState(LoadingStateEnum.LOADED);
      console.error("Resend email error:", err);
    }
  };

  /**
   * Handles retry for failed status checks
   * Implements requirement 6.4 for error recovery
   */
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    loadVerificationStatus();
  };

  // Navigation handlers implementing requirement 6.4 for next steps

  const handlePhoneVerificationClick = () => {
    if (onPhoneVerificationClick) {
      onPhoneVerificationClick();
    } else {
      navigate('/phone-verification', { state: { email } });
    }
  };

  const handleLoginClick = () => {
    if (onLoginClick) {
      onLoginClick();
    } else {
      navigate('/login');
    }
  };

  const handleDashboardClick = () => {
    navigate('/dashboard');
  };

  // Render loading state
  if (loadingState === LoadingStateEnum.LOADING) {
    return (
      <>
        <AuthNavbar />
        <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
          <motion.div
            className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="flex flex-col items-center">
              <LoadingState />
              <p className="text-[color:var(--muted-foreground)] mt-4 text-center">
                Checking your verification status...
              </p>
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  // Render error state with enhanced retry mechanism (requirement 6.4)
  if (loadingState === LoadingStateEnum.ERROR || !verificationData) {
    return (
      <>
        <AuthNavbar />
        <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
          <motion.div
            className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="flex flex-col items-center mb-6">
              <motion.div
                className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <motion.svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-red-600"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </motion.svg>
              </motion.div>

              <motion.h2
                className="text-2xl font-bold text-[color:var(--foreground)] text-center"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                Unable to Load Status
              </motion.h2>

              <motion.p
                className="text-sm text-[color:var(--muted-foreground)] mt-2 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                {error || "Failed to check verification status"}
              </motion.p>

              {retryCount > 0 && (
                <motion.p
                  className="text-xs text-[color:var(--muted-foreground)] mt-1 text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  Retry attempt: {retryCount}
                </motion.p>
              )}
            </div>

            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <motion.button
                onClick={handleRetry}
                className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {retryCount > 0 ? "Try Again" : "Retry"}
              </motion.button>

              <motion.div
                className="text-center space-y-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <Link
                  to="/signup"
                  className="block text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] font-medium"
                >
                  Back to signup
                </Link>
                <Link
                  to="/login"
                  className="block text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] font-medium"
                >
                  Go to login
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </>
    );
  }

  // Determine verification progress and next steps
  const { email_verified, phone_verified, can_access_dashboard } = verificationData;
  
  // Calculate progress percentage
  const totalSteps = 2; // Email and phone verification
  let completedSteps = 0;
  if (email_verified) completedSteps++;
  if (phone_verified) completedSteps++;
  const progressPercentage = (completedSteps / totalSteps) * 100;

  // Determine current status and next steps based on requirements
  let statusTitle = "";
  let statusDescription = "";
  let nextStepTitle = "";
  let nextStepDescription = "";
  let showEmailResend = false;
  let showPhoneVerify = false;
  let showLogin = false;
  let showDashboard = false;

  if (!email_verified) {
    // Requirement 6.1: Email verification required
    statusTitle = "Email Verification Required";
    statusDescription = "Please verify your email address to continue with registration.";
    nextStepTitle = "Verify Your Email";
    nextStepDescription = "Check your inbox for a verification link and click it to verify your email address.";
    showEmailResend = true;
  } else if (!phone_verified) {
    // Requirement 6.2: Phone verification required
    statusTitle = "Phone Verification Required";
    statusDescription = "Your email is verified! Now please verify your phone number to complete registration.";
    nextStepTitle = "Verify Your Phone";
    nextStepDescription = "Enter your phone number and verify it with an SMS code to access your dashboard.";
    showPhoneVerify = true;
  } else if (can_access_dashboard) {
    // Requirement 6.3: Fully verified
    statusTitle = "Verification Complete";
    statusDescription = "Congratulations! Your account is fully verified and ready to use.";
    nextStepTitle = "Access Your Dashboard";
    nextStepDescription = "You can now access all features of your dashboard or continue to login.";
    showDashboard = true;
    showLogin = true;
  }

  // Main verification status display
  return (
    <>
      <AuthNavbar />
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
        <motion.div
          className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Header with status icon and title */}
          <div className="flex flex-col items-center mb-8">
            <motion.div
              className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                can_access_dashboard 
                  ? 'bg-green-100' 
                  : email_verified 
                    ? 'bg-blue-100' 
                    : 'bg-yellow-100'
              }`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={
                  can_access_dashboard 
                    ? 'text-green-600' 
                    : email_verified 
                      ? 'text-blue-600' 
                      : 'text-yellow-600'
                }
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {can_access_dashboard ? (
                  <path d="M20 6L9 17l-5-5"></path>
                ) : email_verified ? (
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                ) : (
                  <>
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </>
                )}
              </motion.svg>
            </motion.div>

            <motion.h2
              className="text-2xl font-bold text-[color:var(--foreground)] text-center"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {statusTitle}
            </motion.h2>

            <motion.p
              className="text-sm text-[color:var(--muted-foreground)] mt-2 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              {statusDescription}
            </motion.p>
          </div>

          {/* Progress Indicator - Visual progress tracking */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[color:var(--foreground)]">
                Verification Progress
              </span>
              <span className="text-sm text-[color:var(--muted-foreground)]">
                {completedSteps}/{totalSteps} completed
              </span>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2">
              <motion.div
                className="bg-[color:var(--color-secondary)] h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.8, delay: 0.6 }}
              />
            </div>

            {/* Verification Steps List */}
            <div className="mt-4 space-y-3">
              <div className="flex items-center">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 ${
                  email_verified ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {email_verified ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-green-600"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                  ) : (
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  )}
                </div>
                <span className={`text-sm ${
                  email_verified 
                    ? 'text-green-600 font-medium' 
                    : 'text-[color:var(--muted-foreground)]'
                }`}>
                  Email Verification {email_verified ? '✓' : ''}
                </span>
              </div>

              <div className="flex items-center">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 ${
                  phone_verified ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {phone_verified ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-green-600"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                  ) : (
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  )}
                </div>
                <span className={`text-sm ${
                  phone_verified 
                    ? 'text-green-600 font-medium' 
                    : 'text-[color:var(--muted-foreground)]'
                }`}>
                  Phone Verification {phone_verified ? '✓' : ''}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Next Steps Section */}
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            {/* Next Step Information - Requirement 6.4 */}
            <div className={`p-4 rounded-md border ${
              can_access_dashboard 
                ? 'bg-green-50 border-green-200' 
                : 'bg-blue-50 border-blue-200'
            }`}>
              <p className={`text-sm font-medium ${
                can_access_dashboard ? 'text-green-800' : 'text-blue-800'
              }`}>
                <strong>{nextStepTitle}</strong>
              </p>
              <p className={`text-sm mt-1 ${
                can_access_dashboard ? 'text-green-700' : 'text-blue-700'
              }`}>
                {nextStepDescription}
              </p>
            </div>

            {/* Success/Error Messages - Requirement 6.5 */}
            {success && (
              <motion.div
                className="p-3 bg-green-50 border border-green-200 rounded-md text-sm"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center">
                  <div className="shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-green-600"
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                  </div>
                  <span className="text-green-600 font-medium">{success}</span>
                </div>
              </motion.div>
            )}

            {error && (
              <motion.div
                className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-start">
                  <div className="shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-2 mt-0.5">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-red-600"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </div>
                  <span className="font-medium">{error}</span>
                </div>
              </motion.div>
            )}

            {/* Action Buttons based on verification status */}
            <div className="space-y-3">
              {/* Requirement 6.1: Email resend option */}
              {showEmailResend && (
                <motion.button
                  onClick={handleResendEmailVerification}
                  disabled={loadingState === LoadingStateEnum.RESENDING_EMAIL}
                  className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loadingState === LoadingStateEnum.RESENDING_EMAIL ? "Sending..." : "Resend Verification Email"}
                </motion.button>
              )}

              {/* Requirement 6.2: Phone verification option */}
              {showPhoneVerify && (
                <motion.button
                  onClick={handlePhoneVerificationClick}
                  className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Verify Phone Number
                </motion.button>
              )}

              {/* Requirement 6.3: Dashboard access when fully verified */}
              {showDashboard && (
                <motion.button
                  onClick={handleDashboardClick}
                  className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Go to Dashboard
                </motion.button>
              )}

              {showLogin && (
                <motion.button
                  onClick={handleLoginClick}
                  className="w-full py-2 px-4 border border-[color:var(--border)] text-[color:var(--foreground)] rounded-md text-sm font-medium hover:bg-[color:var(--muted)]/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Continue to Login
                </motion.button>
              )}
            </div>

            {/* Auto-refresh button for checking verification progress */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              <button
                onClick={() => loadVerificationStatus(true)}
                className="text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] font-medium underline"
              >
                ↻ Refresh Status
              </button>
            </motion.div>

            {/* Navigation Links */}
            <motion.div
              className="text-center space-y-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              {!can_access_dashboard && (
                              <Link
                to="/signup"
                className="block text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] font-medium"
              >
                ← Back to signup
              </Link>
              )}
              <Link
                to="/login"
                className="block text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] font-medium"
              >
                Go to login
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </>
  );
};

export default VerificationStatus;